import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const EMPTY_FORM = { nombre: '' };

const Tecnologias = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/tecnologias?all=1');
            setItems(data);
        } catch {
            setError('Error cargando tecnologías');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setError('');
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({ nombre: item.nombre });
        setEditingId(item.id);
        setError('');
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            if (editingId) {
                await api.put(`/tecnologias/${editingId}`, form);
                showSuccess('Tecnología actualizada correctamente');
            } else {
                await api.post('/tecnologias', form);
                showSuccess('Tecnología creada correctamente');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando tecnología');
        } finally {
            setSaving(false);
        }
    };

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Tecnologías</h2>
                {can('tecnologias', 'create') && (
                    <button
                        onClick={openCreate}
                        className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                    >
                        + Agregar Nueva
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No hay tecnologías registradas</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Nombre</th>
                                {can('tecnologias', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800">{item.nombre}</td>
                                    {can('tecnologias', 'update') && (
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => openEdit(item)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                                            >
                                                Editar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Editar tecnología' : 'Nueva tecnología'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}
                            <div>
                                <label className={labelClass}>Nombre *</label>
                                <input
                                    name="nombre"
                                    value={form.nombre}
                                    onChange={e => setForm({ nombre: e.target.value })}
                                    className={inputClass}
                                    placeholder="Ej: Fibra Optica"
                                />
                            </div>
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setModalOpen(false)} className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                Cancelar
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear tecnología')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tecnologias;
